<template>
  <div class="relative bg-white rounded-2xl shadow-md p-6 mx-auto space-y-2">
    <div class="flex justify-end">
      <StoreConfigurationStatusBadge :configuration-status="store.contactInformationsConfigStatus" />
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
      <StackedField label="Email du magasin">
        <template v-if="editMode">
          <Checkbox
            class="mb-2"
            id="noSellerEmail"
            name="noSellerEmail"
            :label="t('no_seller_email_label')"
            labelClass="font-normal"
            v-model="noSellerEmail"
          />
          <template v-if="!noSellerEmail">
            <Loading v-if="loadingUsers" />
            <template v-else-if="!sellersProfileIdOptions.length">
              <div class="text-sm text-gray-500 mb-1">
                Renseignez l'adresse email à laquelle les vendeurs ont accès.
              </div>
              <FormField id="sellerEmail" name="email" placeholder="john@doe.com" v-model="formData.sellerEmail" />
            </template>
            <template v-else>
              <div class="text-sm text-gray-500 mb-1">Sélectionnez un utilisateur avec le role vendeur</div>
              <Selector
                id="sellersProfileId"
                name="sellersProfileId"
                :options="sellersProfileIdOptions"
                v-model="formData.sellersProfileId"
              />
            </template>
          </template>
        </template>
        <template v-else>{{ sellersProfile?.email || "—" }}</template>
      </StackedField>
    </div>
    <hr class="my-3" />
    <p class="font-bold text-lg mb-2">Référent</p>
    <div v-if="editMode">
      <p class="text-sm text-gray-500 mb-2">
        Renseignez les informations du référent. Ces informations sont optionnelles pour la validation du magasin.
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
        <StackedField label="Nom">
          <FormField id="referent_last_name" name="referent_last_name" v-model="formData.referent.lastName" />
        </StackedField>
        <StackedField label="Prénom">
          <FormField id="referent_first_name" name="referent_first_name" v-model="formData.referent.firstName" />
        </StackedField>
        <StackedField label="Email">
          <FormField id="referent_email" name="referent_email" placeholder="Email" v-model="formData.referent.email" />
        </StackedField>
        <StackedField label="Téléphone">
          <FormField
            id="referent_phone"
            name="referent_phone"
            placeholder="06 12 34 56 78"
            v-model="formData.referent.phone"
          />
        </StackedField>
        <StackedField label="Poste du référent">
          <Selector id="referentRole" name="referentRole" :options="roles" v-model="formData.referentRole" />
        </StackedField>
      </div>
    </div>
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
      <StackedField label="Nom">
        <span v-if="referent">{{ referent?.firstName }} {{ referent?.lastName }}</span>
        <span v-else>—</span>
      </StackedField>
      <StackedField label="Email" :value="referent?.email" />
      <StackedField label="Téléphone" :value="referent?.phone" />
      <StackedField label="Poste du référent" :value="referentRole" />
    </div>
    <div class="flex flex-row-reverse gap-x-4">
      <template v-if="editMode">
        <SecondaryButton @click="editMode = false">Annuler</SecondaryButton>
        <SecondaryButton @click="submitEdit">Mettre à jour</SecondaryButton>
      </template>
      <template v-else>
        <SecondaryButton v-if="validationNeeded" @click="validate">Valider</SecondaryButton>
        <SecondaryButton v-if="validationNeeded" @click="reject">Refuser</SecondaryButton>
        <SecondaryButton @click="editMode = true">Modifier</SecondaryButton>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { StackedField, SecondaryButton, Loading, Selector, FormField, Checkbox } from "@estaly/ui";
import { useI18n } from "vue-i18n";
import StoreConfigurationStatusBadge from "@/components/StoreConfigurationStatusBadge.vue";

export default {
  components: {
    FormField,
    Loading,
    StoreConfigurationStatusBadge,
    SecondaryButton,
    StackedField,
    Selector,
    Checkbox,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: ["store", "isStoreSetup"],

  data: () => {
    return {
      editMode: false,
      noSellerEmail: false,
      formData: {
        sellersProfileId: null,
        sellerEmail: null,
        referentEmail: null,
        referentRole: "",
        referentFirstName: "",
        referentLastName: "",
        referentPhone: "",
      },
      roles: [],
      loadingUsers: false,
    };
  },

  mounted() {
    this.formData = {
      sellersProfileId: this.store.sellersProfile?.id,
      sellerEmail: this.store.sellersProfile?.email,
      referent: {
        lastName: this.store.referent?.lastName,
        firstName: this.store.referent?.firstName,
        phone: this.store.referent?.phone,
        email: this.store.referent?.email,
      },
      referentRole: this.store.referentRole,
    };
    this.roles = [
      {
        value: "store_manager",
        label: this.$t("store.referent_role_values.store_manager"),
      },
      {
        value: "director",
        label: this.$t("store.referent_role_values.director"),
      },
      {
        value: "franchise_owner",
        label: this.$t("store.referent_role_values.franchise_owner"),
      },
    ];
  },

  computed: {
    ...mapState("merchantProfiles", { users: (state) => state.index }),

    pendingValidation() {
      return this.store.contactInformationsConfigStatus === "pending_validation";
    },

    validationNeeded() {
      return this.isStoreSetup && this.pendingValidation;
    },

    sellersProfile() {
      return this.store.sellersProfile;
    },

    referent() {
      return this.store.referent;
    },

    referentRole() {
      if (!this.store.referentRole) return "";

      return this.$t(`store.referent_role_values.${this.store.referentRole}`);
    },

    sellers() {
      return this.users.filter((user) => user.role === "seller");
    },

    sellersProfileIdOptions() {
      return this.sellers.map((user) => ({
        value: user.id,
        label: user.email,
      }));
    },

    selectedSellerProfile() {
      return this.sellers.find((user) => user.id === this.formData.sellersProfileId);
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("merchantProfiles", ["fetchIndex"]),
    ...mapActions("stores", ["updateContactInformations", "update"]),

    validate() {
      this.$emit("validate");
    },

    reject() {
      this.$emit("reject");
    },

    async submitEdit() {
      if (!this.noSellerEmail && !this.selectedSellerProfile?.email && !this.formData.sellerEmail) {
        this.notify({
          category: "simple",
          type: "error",
          title: "Adresse email vendeur",
          text: "Saisissez l'email vendeur ou cochez la case pour indiquer qu'il n'y en a pas.",
        });
        return;
      }
      const email = this.noSellerEmail ? null : this.selectedSellerProfile?.email || this.formData.sellerEmail;
      const response = this.isStoreSetup
        ? await this.updateContactInformations({
            storeId: this.store.id,
            referentRole: this.formData.referentRole,
            email,
            referent: {
              lastName: this.formData.referent?.lastName,
              firstName: this.formData.referent?.firstName,
              email: this.formData.referent?.email,
              phone: this.formData.referent?.phone,
            },
          })
        : await this.update({ storeId: this.store.id, data: this.formData });
      if (!response?.success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Mise à jour effectuée",
      });
      this.$emit("updated");
      this.editMode = false;
    },

    async refreshStoreUsers() {
      this.loadingUsers = true;
      await this.fetchIndex({ storeIds: [this.store.id], roles: ["seller"] });
      this.loadingUsers = false;
    },

    resetFormDataFromStore() {
      const hasSellerProfile = this.store.sellersProfile?.id != null;
      const sellersProfileId = hasSellerProfile ? this.store.sellersProfile.id : null;
      this.noSellerEmail = false;
      this.formData = {
        sellersProfileId,
        sellerEmail: hasSellerProfile ? null : (this.store.sellersProfile?.email ?? null),
        referent: {
          lastName: this.store.referent?.lastName ?? "",
          firstName: this.store.referent?.firstName ?? "",
          phone: this.store.referent?.phone ?? "",
          email: this.store.referent?.email ?? "",
        },
        referentRole: this.store.referentRole ?? "",
      };
    },
  },

  watch: {
    async editMode(value) {
      if (value) {
        this.resetFormDataFromStore();
        await this.refreshStoreUsers();
      }
    },

    store: {
      handler(store) {
        if (this.editMode && store) this.resetFormDataFromStore();
      },
      deep: true,
    },

    noSellerEmail(checked) {
      if (checked) {
        this.formData.sellerEmail = null;
        this.formData.sellersProfileId = null;
      }
    },

    // Ensure mutual exclusivity between sellerEmail and sellersProfileId
    "formData.sellerEmail"(newValue) {
      if (newValue && this.formData.sellersProfileId) {
        this.formData.sellersProfileId = null;
      }
    },

    "formData.sellersProfileId"(newValue) {
      if (newValue && this.formData.sellerEmail) {
        this.formData.sellerEmail = null;
      }
    },
  },
};
</script>

<i18n src="./StoreConfigurationContactInformations.i18n.json"></i18n>
